import { CheckCircle2, Clock, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useLockStore } from "../store/lockStore";
import { useLockActions } from "../hooks/useLockActions";

interface RequestAccessOverlayProps {
  formId: string;
}

const HEADING_ID = "request-access-overlay-heading";

/**
 * Replaces the LockScreen content when the caller has clicked "Request Access"
 * and is waiting for the holder to respond (or for the 30s timer to auto-grant).
 *
 * Two display modes based on outgoingRequestStatus:
 *  - default ("Pending" / null): "Waiting for Response" with countdown + Cancel
 *  - "Granted": brief success message + spinner before the form loads. The
 *    transition timer lives in useLockManager — this component just renders
 *    whatever state it's told.
 */
export function RequestAccessOverlay({ formId }: RequestAccessOverlayProps) {
  const lockedBy = useLockStore((s) => s.lockedBy);
  const countdownSeconds = useLockStore((s) => s.countdownSeconds);
  const outgoingRequestStatus = useLockStore((s) => s.outgoingRequestStatus);
  const { cancelRequest } = useLockActions(formId);

  const isGranted = outgoingRequestStatus === "Granted";

  // Move keyboard focus to the Cancel button on mount (no-op when isGranted
  // since the button isn't rendered then — granted is a brief transitory state).
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isGranted) cancelButtonRef.current?.focus();
  }, [isGranted]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={HEADING_ID}
      className="min-h-screen flex items-center justify-center bg-gray-50 px-6"
    >
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
        {isGranted ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-brand-green-light flex items-center justify-center mb-4">
              <CheckCircle2 size={26} className="text-brand-green" />
            </div>
            <h1 id={HEADING_ID} className="text-2xl font-bold text-brand-navy mb-2">Access Granted</h1>
            <p className="text-gray-600 mb-6">
              <span className="font-semibold text-gray-800 break-words">{lockedBy ?? "The previous user"}</span> has granted access to this form. Going to the form now...
            </p>
            <div className="flex items-center justify-center text-brand-green" aria-live="polite">
              <Loader2 size={28} className="animate-spin" />
            </div>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-brand-green-light flex items-center justify-center mb-4">
              <Clock size={26} className="text-brand-green animate-pulse" />
            </div>

            <h1 id={HEADING_ID} className="text-2xl font-bold text-brand-navy mb-2">Waiting for Response</h1>

            <p className="text-gray-600 mb-1">
              Waiting for <span className="font-semibold text-gray-800 break-words">{lockedBy ?? "the current user"}</span> to respond...
            </p>
            <p className="text-sm text-gray-500 mb-4">
              If they don't respond, you will automatically gain access when the timer expires.
            </p>

            <div className="text-4xl font-bold text-brand-navy mb-6 tabular-nums" aria-live="polite">
              {countdownSeconds ?? "—"}
              <span className="text-base font-medium text-gray-500 ml-1">s</span>
            </div>

            <button
              ref={cancelButtonRef}
              type="button"
              onClick={cancelRequest}
              className="w-full py-2.5 px-4 rounded-md border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-50 cursor-pointer"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
